import React, {useEffect, useState, useMemo} from "react";
import styled from "styled-components/macro";

import {
    Paper as MuiPaper,
    Box,
    Grid as MuiGrid,
    Typography as MuiTypography,
    withStyles, TableCell, TableRow, TableHead, TableBody, Table, Tooltip, TableContainer,
} from "@material-ui/core";

import { spacing } from "@material-ui/system";
import {GenericSidePanel} from "../../../../components/SidePanel/GenericSidePanel.comp";
import {useAuth} from "../../../../context/auth";
import LoaderWithBackDrop from "../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {
    addTrueKeyInState,
    alphaNumericSort,
    predefinedSort,
    getUniqueValues,
    setSingleKeyTrueInState,
    createLcKey, isDQWEnabled,
    splitList,
    addItemToState,
    removeItemFromState
} from "../../../../utils/utils";
import MonthRangePicker from "../../../../components/DatePickers/MonthRangePicker.comp";
import {LinearProgressWithLabel} from "../../../SchoolDashboard/Enhancement/components/LinearProgressWithLabel.comp";
import {getUniqueModuleAssessL6CodesService, getModuleIndicatorTranslationsService, getEfficacyBreakdownService, getSchools} from "../../../../services/school.service";
import {getClasses} from "../../../../services/analysis.service";
import {X} from "react-feather";
import {CustomSelect} from "../../../SchoolDashboard/Enhancement/components/Select.comp";
import {DQW_DQC_MODULES} from "../../../../utils/constants";
import {useLocation} from "react-router-dom";
import {GET_APP_LOCALIZE_TEXT} from "../../../../utils/dq_lc_service/lc_service";
import {useGlobalState} from "../../../../context/global_state";

const CustomTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: '#233044',
        color: theme.palette.common.white,
        padding: 10,
        minWidth: 155,
        fontSize: 11,
        fontWeight: 'bold',
        whiteSpace: 'pre-line',
    },
    body: {
        padding: 10,
        minWidth: 155,
        fontSize: 12,
        whiteSpace: 'pre-line',
    },
}))(TableCell);

const CustomTableRow = styled(TableRow)`
  &:nth-of-type(odd) {
    background-color: rgba(0, 0, 0, 0.025);
  }
`;

const Grid = styled(MuiGrid)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Typography = styled(MuiTypography)(spacing);

const ColouredTypography = styled(Typography)`
  color: ${(props) => props.customcolor ? props.customcolor : props.theme.sidebar.background};
  font-size: ${(props) => props.size ? props.size : "default"};
  text-align: ${(props) => props.textAlign ? props.textAlign : "left"};
`

const Spacer = styled.div(spacing);

const DefaultColouredTypography = styled(Typography)`
  color: ${(props) => props.customcolor ? props.customcolor : props.theme.sidebar.background};
  font-size: ${(props) => props.size ? props.size : "default"};
  font-family: "Lemon/Milk";
`

let dq_modules = {}

function BreakdownEfficacyTab({skill, title, description}) {

    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];
    const { settings } = useGlobalState();
    const module_code = settings?.selected_report;

    const {authUser} = useAuth();
    const {org_code, institute_code, org_obj} = authUser;
    const dqwEnabled = isDQWEnabled(org_obj);

    const SELECT_ALL_KEY = 'SELECT_ALL_INDICATORS';
    const [firstLoad, setFirstLoad] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectorsData, setSelectorsData] = useState(undefined);
    const [pageData, setPageData] = useState(undefined);
    const [tableHeaderData, setTableHeaderData] = useState([]);
    const [tableData, setTableData] = useState([]);

    //indicators data
    const [modules, setModules] = useState([]);
    const [indicators, setIndicators] = useState([]);
    const [selectedIndicators, setSelectedIndicators] = useState([]);
    const [indicatorCheckboxState, setIndicatorCheckboxState] = useState({});
    const [sidePanelKey, setSidePanelKey] = useState(0);

    // Postal code selection
    const subListDivision = 3;
    const [postalCodes, setPostalCodes] = useState(undefined);
    const postalHeader = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_postal") || "Select a Postal Code";
    const [defaultPostal, setDefaultPostal] = useState([]);
    const [additionalPostal, setAdditionalPostal] = useState([]);
    const [selectedPostal, setSelectedPostal] = useState({postal: undefined});
    const [currentInstituteCode, setCurrentInstituteCode] = useState(institute_code);

    // Time Period selection
    const timeHeader = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_time");
    const competencyHeader = GET_APP_LOCALIZE_TEXT(pageLc, "efficacy__select_competency") || "Select a Competency"
    const MIN_USERS = 10; // threshold to enable selection
    const [timePeriods, setTimePeriods] = useState([]);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState([undefined, undefined]);
    const [minDate, setMinDate] = useState(undefined);
    const [maxDate, setMaxDate] = useState(undefined);
    const [timePeriodsReset, setTimePeriodsReset] = useState(false);
    const [resetKey, setResetKey] = useState(false);
    const [timeCheckboxState, setTimeCheckboxState] = useState({});

    const [timeState, setTimeState] = useState(undefined);

    const selectAllYears = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__all_year");

    // Get all active indicators
    const allActiveIndicators = useMemo(() => {
        return modules.flatMap(module => 
            (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
        );
    }, [modules]);

    const getDateRangeOfYears = (years) => {
        const [startYear, endYear] = [years[0], years[years.length - 1]];
        const [startDate, endDate] = [new Date(`${startYear}-01-01`).toISOString(), new Date(`${endYear}-12-31`).toISOString()]
        return [startDate, endDate]
    }

    const getModuleAlias = (code) => {
        const alias = dq_modules[code] ? dq_modules[code].code : undefined;
        if(alias) return alias;
        return code;
    }

    useEffect(() => {
        if(!selectorsData && !firstLoad)
        {
            setTableHeaderData([])
            setTableData([])
            setSelectedTimePeriod([undefined, undefined]);
            setSelectedPostal({postal: undefined});
            setPostalCodes(undefined);
            setTimePeriods([]);
            setMinDate(undefined);
            setMaxDate(undefined);
        }
    }, [selectorsData])

    useEffect(() => {
        if (module_code) {
            uniqueAssessL6Codes().then(r => console.log('Unique L6 Codes Fetched!', r));
        }
    }, [module_code]);

    // useEffect(() => {
    //     if(firstLoad) {
    //         initializeDQModules();
    //         if (module_code) {
    //             if (institute_code) {
    //                 setCurrentInstituteCode(institute_code);
    //                 loadPostalCodes(institute_code);
    //             } else {
    //                 loadFirstSchoolAndPostalCodes();
    //             }
    //         }
    //         setFirstLoad(false);
    //     }
    // }, []);

    // useEffect(() => {
    //     if (module_code) {
    //         const effectiveInstituteCode = institute_code || currentInstituteCode;
    //         if (effectiveInstituteCode) {
    //             setCurrentInstituteCode(effectiveInstituteCode);
    //             loadPostalCodes(effectiveInstituteCode);
    //         } else {
    //             loadFirstSchoolAndPostalCodes();
    //         }
    //     } else {
    //         setDefaultPostal([]);
    //         setAdditionalPostal([]);
    //         setSelectedPostal({postal: undefined});
    //     }
    // }, [module_code, institute_code]);

    useEffect(() => {
        console.log('BreakdownEfficacyTab - Indicator/TimePeriod/Postal changed:', {
            selectedIndicators,
            selectedTimePeriod,
            selectedPostal,
            hasIndicators: selectedIndicators.length > 0,
            hasTimePeriod: selectedTimePeriod[0] && selectedTimePeriod[1]
        });
        if (selectedIndicators.length > 0 && selectedTimePeriod[0] && selectedTimePeriod[1]) {
            console.log('BreakdownEfficacyTab - Loading page data with indicators:', selectedIndicators);
            loadPageData();
        } else if (selectedIndicators.length === 0 || !selectedTimePeriod[0] || !selectedTimePeriod[1]) {
            console.log('BreakdownEfficacyTab - Clearing page data - missing requirements');
            setPageData(undefined);
        }
    }, [selectedIndicators, selectedTimePeriod, selectedPostal])

    useEffect(() => {
        if (allActiveIndicators.length > 0) {
            const allSelected = allActiveIndicators.every(code => selectedIndicators.includes(code));
            setIndicatorCheckboxState(prevState => ({
                ...prevState,
                [SELECT_ALL_KEY]: allSelected
            }));
        }
    }, [selectedIndicators, allActiveIndicators])

    useEffect(() => {
        if (allActiveIndicators.length > 0 && selectedIndicators.length === 0) {
            setSelectedIndicators(allActiveIndicators);
            const newState = { [SELECT_ALL_KEY]: true };
            allActiveIndicators.forEach(code => {
                newState[code] = true;
            });
            setIndicatorCheckboxState(newState);
        }
    }, [allActiveIndicators])

    useEffect(() => {
        // Initialize time checkbox state with "All Year" selected
        if (timePeriods.length > 0) {
            const newTimeState = Object.fromEntries([selectAllYears, ...timePeriods].map(
                (time) => time === selectAllYears ? [time, true] : [time, false]
            ));
            setTimeCheckboxState(newTimeState);
        }
    }, [timePeriods, selectAllYears])

    // Force remount when both states are ready (only once)
    const [initialStatesSet, setInitialStatesSet] = useState(false);
    useEffect(() => {
        const timeStateReady = Object.keys(timeCheckboxState).length > 0;
        const indicatorStateReady = Object.keys(indicatorCheckboxState).length > 0;

        if (timeStateReady && indicatorStateReady && !initialStatesSet) {
            setInitialStatesSet(true);
            setSidePanelKey(prev => prev + 1);
        }
    }, [timeCheckboxState, indicatorCheckboxState, initialStatesSet])

    useEffect(() => {
        if(timePeriods.length > 0)
        {
            const [firstYear, lastYear] = [timePeriods[0], timePeriods[timePeriods.length - 1]]
            setMinDate(new Date(`${firstYear}-01-01`))
            setMaxDate(new Date(`${lastYear}-12-31`))
            selectAllMonthsOfYears(timePeriods);
            setTimePeriodsReset(true);
        }
    },[timePeriods])

    const initializeDQModules = () => {
        dq_modules = DQW_DQC_MODULES(pageLc);
    }

    const uniqueAssessL6Codes = async () => {
        if (!module_code) return;
        try {
            setLoading(true);
            const response = await getUniqueModuleAssessL6CodesService(org_code, module_code);
            if (response.data && response.data.modules) {
                // mark disabled for each indicator based on unique_users_count
                const modulesWithDisabled = (response.data.modules || []).map(module => ({
                    ...module,
                    indicators: (module.indicators || []).map(ind => ({
                        ...ind,
                        disabled: typeof ind.unique_users_count === 'number' ? (ind.unique_users_count < MIN_USERS) : false
                    }))
                }));

                // Extract all module codes and indicator codes
                const allModuleCodes = modulesWithDisabled.map(module => module.module_code);
                const allIndicatorCodes = modulesWithDisabled.flatMap(module => (module.indicators || []).map(ind => ind.indicator_code));

                // Fetch translations with default locale 'en'
                const translationsResponse = await getModuleIndicatorTranslationsService('en', allModuleCodes, allIndicatorCodes);

                if (translationsResponse && translationsResponse.data) {
                    const { modules: moduleTranslationsArray = [], indicators: indicatorTranslationsArray = [] } = translationsResponse.data;

                    // Convert arrays to lookup maps for easier access
                    const moduleTranslationsMap = Object.fromEntries(
                        moduleTranslationsArray.map(m => [m.module_code, m.module_name])
                    );
                    const indicatorTranslationsMap = Object.fromEntries(
                        indicatorTranslationsArray.map(i => [i.indicator_code, i.indicator_name])
                    );

                    // Merge translations into modules and indicators
                    const modulesWithTranslations = modulesWithDisabled.map(module => ({
                        ...module,
                        module_name: moduleTranslationsMap[module.module_code] || module.module_code,
                        indicators: (module.indicators || []).map(ind => ({
                            ...ind,
                            indicator_name: indicatorTranslationsMap[ind.indicator_code] || ind.indicator_code
                        }))
                    }));

                    // Sort modules: enabled indicators first, then disabled
                    const sortedModules = modulesWithTranslations.sort((a, b) => {
                        const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                        const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                        if (aHasEnabled && !bHasEnabled) return -1;
                        if (!aHasEnabled && bHasEnabled) return 1;
                        return 0;
                    });

                    setModules(sortedModules);
                    setIndicators(allIndicatorCodes);
                    // Initialize with all active indicators checked
                    const activeIndicatorCodes = sortedModules.flatMap(module => 
                        (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
                    );
                    setIndicatorCheckboxState(Object.fromEntries([
                        [SELECT_ALL_KEY, true],
                        ...allIndicatorCodes.map(code => [code, activeIndicatorCodes.includes(code)])
                    ]));
                } else {
                    // Fallback if translations not available
                    // Sort modules: enabled indicators first, then disabled
                    const sortedModules = modulesWithDisabled.sort((a, b) => {
                        const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                        const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                        if (aHasEnabled && !bHasEnabled) return -1;
                        if (!aHasEnabled && bHasEnabled) return 1;
                        return 0;
                    });

                    setModules(sortedModules);
                    setIndicators(allIndicatorCodes);
                    // Initialize with all active indicators checked
                    const activeIndicatorCodes = sortedModules.flatMap(module => 
                        (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
                    );
                    setIndicatorCheckboxState(Object.fromEntries([
                        [SELECT_ALL_KEY, true],
                        ...allIndicatorCodes.map(code => [code, activeIndicatorCodes.includes(code)])
                    ]));
                }
            }

        } catch (e) {
            console.log("Error fetching unique L6 codes or translations:", e);
        } finally {
            setLoading(false);
        }
    }

    const loadFirstSchoolAndPostalCodes = async () => {
        if (!module_code) return;
        try {
            setLoading(true);
            const schoolsResult = await getSchools(org_code, module_code, 0, 10, 'asc', undefined);
            if (schoolsResult && schoolsResult.data && schoolsResult.data.data && schoolsResult.data.data.length > 0) {
                const firstSchool = schoolsResult.data.data[0];
                const firstSchoolName = firstSchool?.school_name;
                const firstSchoolCode = firstSchool?.school_code || firstSchool?.institute_code || firstSchoolName;
                if (firstSchoolCode) {
                    setCurrentInstituteCode(firstSchoolCode);
                    await loadPostalCodes(firstSchoolCode);
                }
            }
        } catch (e) {
            console.error('BreakdownEfficacyTab - loadFirstSchoolAndPostalCodes: Error:', e);
        } finally {
            setLoading(false);
        }
    }

    const loadPostalCodes = async (instituteCode = currentInstituteCode || institute_code) => {
        if (!module_code || !instituteCode) {
            return;
        }
        try {
            setLoading(true);
            const result = await getClasses(org_code, instituteCode);
            if (result && result.data && Array.isArray(result.data)) {
                const classes = result.data.map(c => c.class_code || c).filter(Boolean);
                classes.sort((a, b) => a.localeCompare(b));
                const postalSubLists = splitList(classes, subListDivision);
                setDefaultPostal(postalSubLists[0] || []);
                setAdditionalPostal(postalSubLists[1] || []);
                if (postalSubLists[0] && postalSubLists[0].length > 0) {
                    const firstPostal = postalSubLists[0][0];
                    setSelectedPostal({postal: firstPostal});
                }
            }
        } catch (e) {
            console.error('BreakdownEfficacyTab - loadPostalCodes: Error calling getClasses:', e);
            setDefaultPostal([]);
            setAdditionalPostal([]);
        } finally {
            setLoading(false);
        }
    }

    const transformeffficacyData = (effficacyData) => {
        if (!effficacyData || !effficacyData.efficacy_breakdown || !Array.isArray(effficacyData.efficacy_breakdown)) {
            console.log('BreakdownEfficacyTab - transformeffficacyData: Invalid or empty data');
            return {};
        }
        
        // Determine which fields to use based on skill
        let participantsField, skillsField, rateField;
        if (skill === "Acquisition") {
            participantsField = 'unique_users_count';
            skillsField = 'unique_users_count_rub_success';
            rateField = 'avg_assess6_rub';
        } else if (skill === "Improvement") {
            participantsField = 'unique_users_count';
            skillsField = 'unique_users_count_improved';
            rateField = 'avg_assess6_imp_rub';
        } else { // Intention
            participantsField = 'unique_users_count';
            skillsField = 'unique_users_count_changed';
            rateField = 'avg_assess6_ifc_rub';
        }
        
        const transformedData = {};
        const efficacyBreakdown = effficacyData.efficacy_breakdown;
        
        // collect all assess6_codes and group_keys to create Composite Total
        const allAssess6Codes = new Set();
        const categoryGroupKeys = {};
        
        efficacyBreakdown.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key.endsWith('_subgroups') && Array.isArray(item[key])) {
                    // Automatically transform category key to display name
                    const categoryName = key.replace('_subgroups', '')
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    
                    if (!categoryGroupKeys[categoryName]) {
                        categoryGroupKeys[categoryName] = new Set();
                    }
                    
                    item[key].forEach(subgroupItem => {
                        if (subgroupItem.assess6_code) {
                            allAssess6Codes.add(subgroupItem.assess6_code);
                            const groupKey = subgroupItem.group_key?.toString() || 'Unknown';
                            categoryGroupKeys[categoryName].add(groupKey);
                        }
                    });
                }
            });
        });
        
        // Initialize structure with Composite Total - matching GenericEfficacyTab structure
        Object.keys(categoryGroupKeys).forEach(categoryName => {
            transformedData[categoryName] = {
                'Composite Total': {}
            };
            categoryGroupKeys[categoryName].forEach(groupKey => {
                transformedData[categoryName]['Composite Total'][groupKey] = 0;
            });
            allAssess6Codes.forEach(assess6Code => {
                transformedData[categoryName][assess6Code] = {};
                categoryGroupKeys[categoryName].forEach(groupKey => {
                    transformedData[categoryName][assess6Code][groupKey] = 0;
                });
            });
        });
        
        // Second pass: populate actual values and calculate Composite Total
        efficacyBreakdown.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key.endsWith('_subgroups') && Array.isArray(item[key])) {
                    // Automatically transform category key to display name
                    const categoryName = key.replace('_subgroups', '')
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    
                    // Process each subgroup item
                    item[key].forEach(subgroupItem => {
                        if (!subgroupItem.assess6_code) return;
                        
                        const assess6Code = subgroupItem.assess6_code;
                        const groupKey = subgroupItem.group_key?.toString() || 'Unknown';
                        const skills = subgroupItem[skillsField] || 0;
                        
                        // Store as number (matching GenericEfficacyTab structure: data[category][module][subcategory] = number)
                        transformedData[categoryName][assess6Code][groupKey] = skills;
                        
                        // Add to Composite Total for this subcategory
                        transformedData[categoryName]['Composite Total'][groupKey] += skills;
                    });
                }
            });
        });
        
        console.log('BreakdownEfficacyTab - transformeffficacyData: Transformed data:', transformedData);
        return transformedData;
    }

    const loadPageData = async (_time = selectedTimePeriod, _postal = selectedPostal) => {
        console.log('BreakdownEfficacyTab - loadPageData called with:', {
            _time,
            _postal,
            selectedIndicators,
            module_code,
            institute_code: currentInstituteCode || institute_code
        });
        
        const postalValue = _postal?.postal || _postal;
        if (!_time[0] || !_time[1] || !module_code) {
            console.log('BreakdownEfficacyTab - loadPageData: Missing required parameters, skipping');
            setPageData(undefined);
            return;
        }

        setLoading(true);
        try {
            setPageData(undefined);
            const startTime = _time[0] instanceof Date ? _time[0].toISOString() : _time[0];
            const endTime = _time[1] instanceof Date ? _time[1].toISOString() : _time[1];
            
            const postalCodeList = postalValue ? [postalValue] : null;
            
            const result = await getEfficacyBreakdownService(
                org_code,
                module_code,
                selectedIndicators,
                startTime,
                endTime,
                // postalCodeList
            );
            
            if (result && result.data) {
                const data = result.data.data || result.data;
                
                // Transform API data to table structure
                const transformedData = transformeffficacyData(data);
                
                setPageData(transformedData);
                loadGraphData(transformedData);
            }
            else {
                setErrorMessage(GET_APP_LOCALIZE_TEXT(pageLc, "general__data_unavailable"));
            }
        } catch (e) {
            console.error('BreakdownEfficacyTab - Error in loadPageData:', e);
        }
        setTimeout(() => {setLoading(false)}, 200);
        return true;
    }

    function loadGraphData(data)
    {
        console.log('BreakdownEfficacyTab - loadGraphData called with:', data);
        populateTable(data);
    }

    useEffect(() => {
        if (timePeriods.length === 0) {
            const currentYear = new Date().getFullYear();
            const years = [];
            for (let i = 0; i < 5; i++) {
                years.push((currentYear - i).toString());
            }
            setTimePeriods(years.reverse());
        }
    }, []);

    function selectTimePeriod(_timePeriods, fetch = false)
    {
        setSelectedTimePeriod(_timePeriods);
        if(fetch)
        {
            loadPageData(_timePeriods).then(d => console.log('Enhancement Efficacy Fetched - By Selection of Time Period', d));
        }
    }

    function onSelectTimePeriod (item, isChecked, state, setState) {
        if(isChecked)
        {
            if(item === selectAllYears)
            {
                selectAllMonthsOfYears(timePeriods);
            }
            else
            {
                selectAllMonthsOfYears([item]);
            }
            const nextState = Object.fromEntries([selectAllYears, ...timePeriods].map(
                (time) => time === item ? [time, true] : [time, false]
            ));
            setState(nextState);
            setTimeCheckboxState(nextState);
            setResetKey(prevReset => !prevReset);
        }
        else
        {
            console.log("Oops")
            setState({
                ...state,
                [item]: true,
            });
        }
    }

    function onClickDatePicker(event, state, setState) {
        setState(Object.fromEntries([selectAllYears, ...timePeriods].map(
            (time) => [time, false]
        )));
    }

    function onSelectDateRange(dates) {

        const start_date = new Date(Date.parse(dates[0])).toISOString();
        const end_date = new Date(Date.parse(dates[1])).toISOString();
        setSelectedTimePeriod([start_date, end_date]);
    }

    const selectAllMonthsOfYears = (years) => {
        if (!years || years.length === 0) return;

        const [startYear, endYear] = [years[0], years[years.length-1]];
        const [startDate, endDate] = [new Date(`${startYear}-01-01`).toISOString(), new Date(`${endYear}-12-31`).toISOString()]
        setSelectedTimePeriod([startDate, endDate]);
    }

    // Flatten modules and indicators for SidePanel display
    const flattenedData = modules.flatMap(module => {
        const moduleItem = `MODULE:${module.module_name || module.module_code}`;
        const moduleIndicators = (module.indicators || []).map(ind => ind.indicator_code);
        return [moduleItem, ...moduleIndicators];
    });

    const flattenedDataWithSelectAll = [SELECT_ALL_KEY, ...flattenedData];

    // disabled list
    const disabledSecondaryItems = modules.flatMap(module => (module.indicators || []).filter(ind => ind.disabled).map(ind => ind.indicator_code));
    const getIndicatorDisplayName = (indicatorCode) => {
        for (const module of modules) {
            const indicator = (module.indicators || []).find(ind => ind.indicator_code === indicatorCode);
            if (indicator) {
                return indicator.indicator_name || indicatorCode;
            }
        }
        return indicatorCode;
    };

    function onSelectIndicator(item, isChecked, state, setState) {
        if (item === SELECT_ALL_KEY) {
            if (isChecked) {
                const newSelectedIndicators = [...allActiveIndicators];
                setSelectedIndicators(newSelectedIndicators);
                const newState = { ...state, [SELECT_ALL_KEY]: true };
                allActiveIndicators.forEach(code => {
                    newState[code] = true;
                });
                setState(newState);
                setIndicatorCheckboxState(newState);
            } else {
                setSelectedIndicators([]);
                const newState = { ...state, [SELECT_ALL_KEY]: false };
                allActiveIndicators.forEach(code => {
                    newState[code] = false;
                });
                setState(newState);
                setIndicatorCheckboxState(newState);
            }
            return;
        }

        const indicatorCode = item;
        console.log('BreakdownEfficacyTab - onSelectIndicator called:', {
            indicatorCode,
            isChecked,
            currentSelectedIndicators: selectedIndicators
        });
        
        const indicatorObj = modules
            .flatMap(m => m.indicators || [])
            .find(ind => ind.indicator_code === indicatorCode);

        if (indicatorObj?.disabled) {
            console.log('BreakdownEfficacyTab - Indicator is disabled, ignoring:', indicatorCode);
            return;
        }

        // Update local selection
        if (isChecked) {
            setSelectedIndicators(prev => {
                const newIndicators = [...prev, indicatorCode];
                console.log('BreakdownEfficacyTab - Added indicator. New list:', newIndicators);
                const allSelected = allActiveIndicators.length > 0 && allActiveIndicators.every(code =>
                    newIndicators.includes(code)
                );
                
                const newState = {
                    ...state,
                    [indicatorCode]: true,
                    [SELECT_ALL_KEY]: allSelected
                };
                setState(newState);
                setIndicatorCheckboxState(prevState => ({
                    ...prevState,
                    [indicatorCode]: true,
                    [SELECT_ALL_KEY]: allSelected
                }));
                
                return newIndicators;
            });
        } else {
            setSelectedIndicators(prev => {
                const newIndicators = prev.filter(code => code !== indicatorCode);
                console.log('BreakdownEfficacyTab - Removed indicator. New list:', newIndicators);
                const newState = {
                    ...state,
                    [indicatorCode]: false,
                    [SELECT_ALL_KEY]: false
                };
                setState(newState);
                setIndicatorCheckboxState(prevState => ({
                    ...prevState,
                    [indicatorCode]: false,
                    [SELECT_ALL_KEY]: false
                }));
                
                return newIndicators;
            });
        }
    }

    function onSelectPostal(item, isChecked, state, setState) {
        if(isChecked) {
            setSelectedPostal({postal: item})
            setState(Object.fromEntries(defaultPostal.map(
                (postal) => postal === item ? [postal, true] : [postal, false]
            )));
        }
        else
        {
            setState({
                ...state,
                [item]: true
            });
        }
    }

    function onAddPostal(event, state, setState) {
        const item = event.target.value;
        removeItemFromState(item, additionalPostal, setAdditionalPostal);
        addItemToState(item, defaultPostal, setDefaultPostal);
        setState({
            ...Object.fromEntries(defaultPostal.map((postal) => [postal, false])),
            [item]: true,
        });
        setSelectedPostal({postal: item});
    }

    function onRemovePostal(item, state, setState) {
        const newDefaultPostal = removeItemFromState(item, defaultPostal, setDefaultPostal);
        addItemToState(item, additionalPostal, setAdditionalPostal, true);
        setState({
            ...Object.fromEntries(newDefaultPostal.map((postal, index) => 
                index === 0 ? [postal, true] : [postal, false]
            )),
        });
        if (newDefaultPostal.length > 0) {
            setSelectedPostal({postal: newDefaultPostal[0]});
        } else {
            setSelectedPostal({postal: undefined});
            setPageData(undefined);
        }
    }

    const handleTimePeriodsResetAcknowledged = () => {
        setTimePeriodsReset(false);
    };

    const populateTable = (data) =>
    {
        if(!data) return;
        
        const categories = Object.keys(data).filter((cat) => Object.keys(data[cat]).length !== 0);

        const modules = getUniqueValues(categories.map((category) => Object.keys(data[category])))
            .filter((mod) => dqwEnabled || !mod.toLowerCase().includes("dqw"))

        modules
            .sort(alphaNumericSort(undefined, true))
            .sort(predefinedSort(["Composite Total", ...Object.keys(dq_modules)]))

        const subCategories = categories.map((category) => {
            return getUniqueValues(modules.map((module) => Object.keys(data[category][module] ? data[category][module]: {}))).sort()
        })

        const effficacyData = {}
        categories.forEach((category, cat_index) => {
            effficacyData[category] = {}
            modules.forEach((module) => {
                effficacyData[category][module] = {}
                subCategories[cat_index].forEach((subcat) => {
                    effficacyData[category][module][subcat] = data[category][module] && data[category][module][subcat]
                        ? (typeof data[category][module][subcat] === 'number' ? data[category][module][subcat] : (data[category][module][subcat].count || 0))
                        : 0
                })
            })
        })

        //calculating sums: sum up values for each indicator

        const sumData = {}

        modules.forEach((module) => {
            const category = categories[0];
            const subcategories = subCategories[0];
            let sum = 0;
            if(subcategories) {
                subcategories.forEach((subCategory) => sum += effficacyData[category][module][subCategory])
            }
            sumData[module] = sum;
        })

        //Initializing table data

        let _tableData = categories.map((cat) => {
            return {
                category: cat,
                data: [],
            }
        });

        //filling data from api

        for(let i = 0; i < categories.length; i++)
        {
            let category = categories[i]
            for(let j=0; j < subCategories[i].length; j++)
            {
                let subCategory = subCategories[i][j];
                // values are directly from effficacyData (numbers)
                let module_values = modules.map((module) => effficacyData[category][module][subCategory])
                
                //  percentages calculated as module_value / Composite_Total * 100
                let module_percents = modules.map((module) => {
                    const moduleValue = effficacyData[category][module][subCategory];
                    const compositeTotal = effficacyData[category]["Composite Total"] && effficacyData[category]["Composite Total"][subCategory] 
                        ? effficacyData[category]["Composite Total"][subCategory] 
                        : 0;
                    if (compositeTotal && compositeTotal > 0) {
                        return Math.round((moduleValue / compositeTotal) * 100);
                    } else {
                        return 0;
                    }
                })
                
                //Pushing Data into Final Table - matching GenericEfficacyTab structure exactly
                _tableData[i].data.push(
                    {
                        subcategory: subCategory,
                        values: [...module_values],
                        percents: [...module_percents],
                        total: effficacyData[category]["Composite Total"] && effficacyData[category]["Composite Total"][subCategory] 
                            ? effficacyData[category]["Composite Total"][subCategory] 
                            : 0
                    }
                )
            }
        }

        const _tableHeaderData = []

        modules.forEach((module) => {
            _tableHeaderData.push({
                header: module,
                displayName: getIndicatorDisplayName(module),
                total: sumData[module]
            })
        })

        setTableData(_tableData);
        setTableHeaderData(_tableHeaderData);
    }

    return (
        <Paper container>
            <Grid container>
                <Grid item xs={12} sm={12} md={3}>
                    <GenericSidePanel
                        key={sidePanelKey}
                        data={[
                            [selectAllYears, ...timePeriods],
                            flattenedDataWithSelectAll,
                        ]}
                        headers={[
                            timeHeader,
                            competencyHeader,
                        ]}
                        visible={[true, true]}
                        colourize={[false, false]}
                        defaultCheckIndices={[0, -1]}
                        singleSelectIndices={[0, undefined]}
                        initialCheckboxStates={[
                            timeCheckboxState,
                            indicatorCheckboxState,
                        ]}
                        onCheckCallbacks={[
                            onSelectTimePeriod,
                            onSelectIndicator,
                        ]}
                        footers={[
                            MonthRangePicker,
                            undefined,
                        ]}
                        footersData={[
                            {onChangeDate: onSelectDateRange, minDate, maxDate, resetKey},
                            undefined,
                        ]}
                        footersCallbacks={[
                            {onOpen: onClickDatePicker},
                            undefined,
                        ]}
                        timePeriodsReset={timePeriodsReset}
                        onResetAcknowledged={handleTimePeriodsResetAcknowledged}
                        disabledItems={[
                            [],
                            disabledSecondaryItems,
                        ]}
                        labelFormatters={[
                            undefined,
                            (item) => {
                                if (item === SELECT_ALL_KEY) {
                                    return GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_all", "Select All");
                                }
                                return getIndicatorDisplayName(item);
                            },
                        ]}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9} alignItems={!pageData ? 'center' : ''} container={!pageData} justify={'center'} style={{overflow: 'visible'}}>
                    {
                        pageData ?
                            <Paper container={"true"} mt={10} mb={5} style={{width: '100%', overflow: 'visible'}}>
                                <ColouredTypography variant={"body1"} size={"16px"} customcolor={"dimgrey"} textAlign={"center"} mb={5} mx={5}>
                                    {description}
                                </ColouredTypography>
                                <Typography mb={5} variant="h4" gutterBottom align={'center'}>
                                    {title}
                                </Typography>
                                <TableContainer style={{maxWidth: '100%', overflowY: 'auto'}}>
                                <Box mr={10}>
                                    <Grid container spacing={3} style={{alignItems: "center"}}>
                                        <Grid item xs={12} sm={2} md={2}>
                                        </Grid>
                                        <Grid item xs={12} sm={10} md={10}>
                                            <Table size="small" aria-label="a dense table" style={{minWidth: '1000px'}}>
                                                <TableHead>
                                                    {
                                                        <TableRow>
                                                            <CustomTableCell align="center">
                                                                <DefaultColouredTypography variant={"h6"} customcolor={"white"} size={"12px"}>
                                                                </DefaultColouredTypography>
                                                            </CustomTableCell>
                                            {
                                                tableHeaderData.map((item, index) => {
                                                    return (
                                                        <CustomTableCell key={index} align="center">
                                                            {item.displayName || item.header}
                                                        </CustomTableCell>
                                                    )
                                                })
                                            }
                                                        </TableRow>
                                                    }
                                                </TableHead>
                                                <TableBody>
                                                    {
                                                        <CustomTableRow key={"totals"}>
                                                            <CustomTableCell align="center">{GET_APP_LOCALIZE_TEXT(pageLc, "enhancement__totals")}</CustomTableCell>
                                                            {
                                                                tableHeaderData.map((item, index) => (
                                                                    <Tooltip key={index} arrow={true} placement={"top"} title={<Typography>{GET_APP_LOCALIZE_TEXT(pageLc, "assessment__participants")}</Typography>}>
                                                                        <CustomTableCell align="center">{item.total}</CustomTableCell>
                                                                    </Tooltip>
                                                                ))
                                                            }
                                                        </CustomTableRow>
                                                    }
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                    </Grid>
                                    <Spacer my={5}/>
                                    {/* Table rows showing categories with labels on left side */}
                                    {
                                        tableData.map((item, itemIndex) => {
                                            return <Grid key={itemIndex} container spacing={3} style={{alignItems: "center"}}>
                                                <Grid item xs={12} sm={2} md={2}>
                                                    <Typography variant="h4" align={'center'}>
                                                        {item.category}
                                                    </Typography>
                                                    <Spacer my={10}/>
                                                </Grid>
                                                <Grid item xs={12} sm={10} md={10}>
                                                    <Table size="small" aria-label="a dense table" style={{minWidth: '1000px'}}>
                                                        <TableBody>
                                                            {
                                                                item.data.map((subItem, index) => (
                                                                    <CustomTableRow key={index}>
                                                                        <CustomTableCell align="center">{subItem.subcategory}</CustomTableCell>
                                                                        {
                                                                            subItem.percents.map((value, index) => {
                                                                                return <Tooltip key={index} arrow={true} placement={"top"} title={<Typography>{`${subItem.values[index]}/${subItem.total}`}</Typography>}>
                                                                                    <CustomTableCell align={"center"}>
                                                                                        <LinearProgressWithLabel value={isNaN(value) ? 0 : value}/>
                                                                                    </CustomTableCell>
                                                                                </Tooltip>
                                                                            })
                                                                        }
                                                                    </CustomTableRow>
                                                                ))
                                                            }
                                                        </TableBody>
                                                        <Spacer my={10}/>
                                                    </Table>
                                                </Grid>
                                            </Grid>
                                        })
                                    }
                                </Box>
                                </TableContainer>
                            </Paper>
                            :
                            <DefaultColouredTypography variant={"h3"} align={'center'} py={5}>{errorMessage}</DefaultColouredTypography>
                    }
                </Grid>
            </Grid>
            <LoaderWithBackDrop loading={loading}/>
        </Paper>
    );
}

export default BreakdownEfficacyTab;
